import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import {
    type CartData,
    CartErrorKeys,
    type CartUpdatePayload,
    type PersonalDetails,
    type VoucherOptions,
} from '../../interfaces/Cart.model';
import { api } from '../../utils/api';

interface CartState {
    cart: CartData | null;
    loading: boolean;
    updatingItems: number[];
    errors: { [key: string]: string };
    message: string | null;
}

type CartAction =
    | { type: 'SET_CART'; payload: CartData | null }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_UPDATING_ITEM'; payload: number }
    | { type: 'REMOVE_UPDATING_ITEM'; payload: number }
    | { type: 'SET_ERROR'; payload: { key: string; message: string } }
    | { type: 'CLEAR_ERRORS' }
    | { type: 'SET_MESSAGE'; payload: string | null };

const initialState: CartState = {
    cart: null,
    loading: true,
    updatingItems: [],
    errors: {},
    message: null,
};

function cartReducer(state: CartState, action: CartAction): CartState {
    switch (action.type) {
        case 'SET_CART':
            return { ...state, cart: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_UPDATING_ITEM':
            return { ...state, updatingItems: [...state.updatingItems, action.payload] };
        case 'REMOVE_UPDATING_ITEM':
            return {
                ...state,
                updatingItems: state.updatingItems.filter((id) => id !== action.payload),
            };
        case 'SET_ERROR':
            return {
                ...state,
                errors: { ...state.errors, [action.payload.key]: action.payload.message },
            };
        case 'CLEAR_ERRORS':
            return { ...state, errors: {} };
        case 'SET_MESSAGE':
            return { ...state, message: action.payload };
        default:
            return state;
    }
}

interface CartContextType {
    cart: CartData | null;
    errors: { [key: string]: string };
    message: string | null;
    updatingItems: number[];
    paymentHashes: { [key: string]: string } | null;
    updateQuantity: (id: number, qty: number) => Promise<void>;
    updateCart: (personalDetails: PersonalDetails) => Promise<void>;
    addVoucherToCart: (voucherData: VoucherOptions) => Promise<void>;
    addVoucherCode: (code: string) => Promise<void>;
    removeVoucherCode: (code: string, type: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

const buildAddress = (formData: PersonalDetails) => ({
    fullName: formData.fullName,
    addressLine1: formData.address,
    postalCode: formData.postalCode,
    countryCode: formData.countryCode,
    locality: formData.city,
    country: formData.countryCode,
    administrativeArea: formData.administrativeArea || formData.city,
});

const transformToApiFormat = (formData: PersonalDetails): CartUpdatePayload => {
    const address = buildAddress(formData);
    return {
        email: formData.email,
        fullName: formData.fullName,
        fields: {
            naamContactpersoon: formData.fullName,
            telefoon: formData.phone,
            mailAdres: formData.email,
            contactstreet: formData.address,
            contactpostcode: formData.postalCode,
            contactcity: formData.city,
        },
        billingAddress: address,
        shippingAddress: address,
        makePrimaryShippingAddress: formData.makePrimaryShippingAddress,
    };
};

export const CartProvider: React.FC<{ locale: string; children: React.ReactNode }> = ({
    locale,
    children,
}) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);
    const [paymentHashes, setPaymentHashes] = useState<{ [key: string]: string } | null>(null);

    const { t } = useTranslation();

    useEffect(() => {
        i18n.changeLanguage(locale);
    }, [locale]);

    const fetchCart = useCallback(async () => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const data = await api.cart.get();
            dispatch({ type: 'SET_CART', payload: data.cart || null });
        } catch (err) {
            dispatch({
                type: 'SET_ERROR',
                payload: {
                    key: CartErrorKeys.FETCH,
                    message: err instanceof Error ? err.message : 'Failed to fetch cart',
                },
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const fetchHashes = useCallback(async () => {
        if (state.cart) {
            try {
                const hashes = await api.cart.getPaymentHashes();
                setPaymentHashes(hashes);
            } catch (error) {
                console.error('Failed to fetch payment hashes:', error);
            }
        }
    }, [state.cart]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    useEffect(() => {
        fetchHashes();
    }, [fetchHashes]);

    const updateCart = useCallback(async (personalDetails: PersonalDetails) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            dispatch({ type: 'CLEAR_ERRORS' });
            dispatch({ type: 'SET_MESSAGE', payload: null });

            const apiPayload = transformToApiFormat(personalDetails);
            const data = await api.cart.update(apiPayload);

            dispatch({ type: 'SET_CART', payload: data.updateCartCustomer || null });
            dispatch({ type: 'SET_MESSAGE', payload: null });
        } catch (err) {
            // Don't log personalDetails — contains PII (email, phone, address).
            console.error('CartProvider.updateCart error:', {
                errorMessage: err instanceof Error ? err.message : 'Unknown error',
                errorCause: err instanceof Error ? err.cause : undefined,
                errorStack: err instanceof Error ? err.stack : undefined,
            });

            if (err instanceof Error) {
                const errorKey = typeof err.cause === 'string' ? err.cause : CartErrorKeys.UPDATE;
                const errorMessage = err.message;

                dispatch({
                    type: 'SET_ERROR',
                    payload: {
                        key: errorKey,
                        message: errorMessage,
                    },
                });

                const structuredError = {
                    errorKey,
                    errorMessage,
                    errorCause: err.cause,
                };
                throw structuredError;
            }

            const genericError = {
                errorKey: CartErrorKeys.UPDATE,
                errorMessage: 'Failed to update cart',
                errorCause: null,
            };
            throw genericError;
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const updateQuantity = useCallback(async (lineItemId: number, newQty: number) => {
        try {
            dispatch({ type: 'SET_UPDATING_ITEM', payload: lineItemId });
            dispatch({ type: 'CLEAR_ERRORS' });
            dispatch({ type: 'SET_MESSAGE', payload: null });

            const data = await api.cart.updateQuantity(lineItemId, newQty);
            dispatch({ type: 'SET_CART', payload: data.updateCartItem || null });
            dispatch({ type: 'SET_MESSAGE', payload: null });
        } catch (err) {
            dispatch({
                type: 'SET_ERROR',
                payload: {
                    key: CartErrorKeys.QUANTITY,
                    message: err instanceof Error ? err.message : 'Failed to update quantity',
                },
            });
            await api.cart.get(); // Refresh cart on error
        } finally {
            dispatch({ type: 'REMOVE_UPDATING_ITEM', payload: lineItemId });
        }
    }, []);

    const addVoucherToCart = useCallback(async (voucherData: VoucherOptions) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            dispatch({ type: 'CLEAR_ERRORS' });
            dispatch({ type: 'SET_MESSAGE', payload: null });

            const data = await api.cart.addVoucher(voucherData);
            dispatch({ type: 'SET_CART', payload: data.addVoucherCode || null });
            dispatch({ type: 'SET_MESSAGE', payload: null });
        } catch (err) {
            dispatch({
                type: 'SET_ERROR',
                payload: {
                    key: 'voucher',
                    message: err instanceof Error ? err.message : 'Failed to add voucher',
                },
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const addVoucherCode = useCallback(
        async (code: string) => {
            try {
                dispatch({ type: 'SET_LOADING', payload: true });
                dispatch({ type: 'CLEAR_ERRORS' });

                const data = await api.cart.addVoucherCode(code);

                dispatch({ type: 'SET_CART', payload: data.addVoucherCode || null });
                dispatch({ type: 'SET_MESSAGE', payload: t('voucher.applied') });
            } catch (err) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: {
                        key: 'voucher',
                        message: err instanceof Error ? err.message : t('voucher.applyFailed'),
                    },
                });
                throw err;
            } finally {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        },
        [t]
    );

    const removeVoucherCode = useCallback(
        async (code: string, type: string) => {
            try {
                dispatch({ type: 'SET_LOADING', payload: true });
                dispatch({ type: 'CLEAR_ERRORS' });

                const data = await api.cart.removeVoucherCode(code, type);
                dispatch({ type: 'SET_CART', payload: data.removeVoucherCode || null });
                dispatch({ type: 'SET_MESSAGE', payload: t('voucher.removed') });
            } catch (err) {
                dispatch({
                    type: 'SET_ERROR',
                    payload: {
                        key: 'voucher',
                        message: err instanceof Error ? err.message : t('voucher.removalFailed'),
                    },
                });
                throw err;
            } finally {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        },
        [t]
    );

    const value = useMemo(
        () => ({
            ...state,
            paymentHashes,
            updateQuantity,
            updateCart,
            addVoucherToCart,
            addVoucherCode,
            removeVoucherCode,
        }),
        [
            state,
            paymentHashes,
            updateQuantity,
            updateCart,
            addVoucherToCart,
            addVoucherCode,
            removeVoucherCode,
        ]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCartContext must be used within a CartProvider');
    }
    return context;
};
